
import { useEffect, useState } from "react";
import { getAllStates, getCitiesByState } from "@/data/estados-cidades";
import { normalizeUrl } from "@/lib/utils";
import { Helmet } from "react-helmet-async";

interface SitemapProps {
  triggerDownload?: boolean;
}

const BASE_URL = "https://www.localup.com.br";
const TODAY = new Date().toISOString().split('T')[0];

const SitemapXml = ({ triggerDownload = false }: SitemapProps) => {
  const [xmlContent, setXmlContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const generateSitemap = async () => {
      setIsLoading(true);
      try {
        // Links estáticos
        const staticLinks = [
          { url: `${BASE_URL}/`, lastmod: TODAY, changefreq: "monthly", priority: "1.0" },
          { url: `${BASE_URL}/servicos`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
          { url: `${BASE_URL}/sobre`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
          { url: `${BASE_URL}/contato`, lastmod: TODAY, changefreq: "monthly", priority: "0.8" },
          { url: `${BASE_URL}/cidades`, lastmod: TODAY, changefreq: "weekly", priority: "0.9" },
        ];
        
        // Links dinâmicos para estados
        const allStates = getAllStates();
        
        const stateLinks = allStates.map(state => ({
          url: `${BASE_URL}/estado/${state.sigla.toLowerCase()}`,
          lastmod: TODAY,
          changefreq: "weekly",
          priority: "0.7"
        }));
        
        // Links dinâmicos para cidades - processo em lotes para melhorar performance
        let cityLinks = [];
        const batchSize = 100;
        
        for (const state of allStates) {
          const cities = getCitiesByState(state.sigla);
          
          for (let i = 0; i < cities.length; i += batchSize) {
            const batch = cities.slice(i, i + batchSize);
            const batchLinks = batch.map(city => ({
              url: `${BASE_URL}/cidade/local/${normalizeUrl(city)}`,
              lastmod: TODAY,
              changefreq: "weekly",
              priority: "0.6"
            }));
            
            cityLinks = [...cityLinks, ...batchLinks];
          }
        }
        
        // Combinar todos os links
        const allLinks = [...staticLinks, ...stateLinks, ...cityLinks];
        
        // Gerar o XML
        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${allLinks.map(link => `  <url>
    <loc>${link.url}</loc>
    <lastmod>${link.lastmod}</lastmod>
    <changefreq>${link.changefreq}</changefreq>
    <priority>${link.priority}</priority>
  </url>`).join('\n')}
</urlset>`;
        
        setXmlContent(xml);
      } catch (error) {
        console.error("Error generating sitemap:", error);
        setXmlContent("<!-- Error generating sitemap -->");
      } finally {
        setIsLoading(false);
      }
    };
    
    generateSitemap();
  }, []);

  if (isLoading) {
    return null;
  }

  // Para download, retornamos um conteúdo diferente
  if (triggerDownload) {
    return (
      <div>
        <pre>{xmlContent}</pre>
      </div>
    );
  }

  // Para a rota /sitemap.xml, renderizamos com o content-type correto
  return (
    <>
      <Helmet>
        <meta httpEquiv="Content-Type" content="application/xml; charset=utf-8" />
      </Helmet>
      <div dangerouslySetInnerHTML={{ __html: xmlContent }} />
    </>
  );
};

export default SitemapXml;
